<template>
  <!-- Delete Confirmation Modal -->
  <div class="modal fade" id="deleteItemBlockModal" ref="deleteItemBlockModal" tabindex="-1" role="dialog" aria-hidden="true" data-bs-backdrop="true">
    <div class="modal-dialog" role="document">
      <div class="modal-content" v-if="itemBlockToDelete">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t('page.itemBlock.deleteConfirmTitle') }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <p>{{ $t('page.itemBlock.deleteConfirmMessage', { itemBlockName: itemBlockToDelete.name, itemBlockCode: itemBlockToDelete.code }) }}</p>
          <div class="card">
            <div class="card-body">
              <h6 class="card-title">{{ itemBlockToDelete.code }}</h6>
              <p class="card-text">{{ itemBlockToDelete.name }}</p>
              <small class="text-muted">{{ $t('field.generic.linesCount') }}: {{ itemBlockToDelete.partItems.length }}</small>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-danger" @click="confirmDeleteItemBlock" :disabled="isDeleting">
            <div v-if="isDeleting" class="spinner-border spinner-border-sm me-2"></div>
            <font-awesome-icon v-else :icon="['fas', 'trash']" class="me-2" />
            {{ $t('action.delete') }}
          </button>
          <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal" :disabled="isDeleting">
            <font-awesome-icon :icon="['fas', 'xmark']" class="me-2" />
            {{ $t('action.cancel') }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <div class="container-fluid p-0">
    <table v-if="sortedItemBlocks.length > 0" class="table table-hover table-sm table-fit align-middle">
      <thead>
        <tr>
          <th>{{ $t('field.generic.name') }}</th>
          <th class="d-none d-lg-table-cell">{{ $t('field.generic.linesCount') }}</th>
          <th>{{ $t('field.generic.rowActions') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="itemBlock in sortedItemBlocks" :key="itemBlock.id">
          <td style="cursor: pointer" @click="emit('selected', itemBlock.id)">
            <button class="btn btn-link">
              {{ itemBlock.code }}
            </button>
            <span v-if="itemBlock.name?.trim() !== itemBlock.code?.trim()" class="fw-bold text-primary">
              {{ itemBlock.name }}
            </span>
          </td>
          <td class="d-none d-lg-table-cell text-end"><span class="badge text-bg-secondary" :class="{ 'text-bg-info': itemBlock.partItems.length === 0 }">{{ itemBlock.partItems.length }}</span></td>
          <td>
            <button class="btn btn-outline-danger" type="button" @click="showDeleteConfirmation(itemBlock)">
              <font-awesome-icon :icon="['fas', 'trash']" />
            </button>
          </td>
        </tr>
      </tbody>
    </table>
    <span v-else>{{ $t('error.noData') }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { toast } from 'vue3-toastify';
import { Modal } from 'bootstrap';
import { useSessionStore, useItemBlocksStore } from '@/stores';
import { useI18n } from 'vue-i18n';
import type IItemBlock from '@/models/responses/IItemBlock';

const sessionStore = useSessionStore();
const { selectedSiteId } = storeToRefs(sessionStore);

const itemBlocksStore = useItemBlocksStore();
const { data: itemBlocks } = storeToRefs(itemBlocksStore);

const i18n = useI18n();

// Modal related reactive variables
const deleteItemBlockModal = ref<HTMLElement | null>(null);
const itemBlockToDelete = ref<IItemBlock | null>(null);
const isDeleting = ref(false);

const emit = defineEmits<{
  (e: 'selected', id: number): void
}>();

const sortedItemBlocks = computed(() => {
  return [...itemBlocks.value].sort((a, b) => {
    return a.name < b.name ? -1 : 1;
  });
});

const showDeleteConfirmation = (itemBlock: IItemBlock) => {
  itemBlockToDelete.value = itemBlock;
  if (deleteItemBlockModal.value) {
    const modal = new Modal(deleteItemBlockModal.value);
    modal.show();
  }
};

const hideDeleteModal = () => {
  if (deleteItemBlockModal.value) {
    const modal = Modal.getInstance(deleteItemBlockModal.value);
    modal?.hide();
  }
  itemBlockToDelete.value = null;
};

const confirmDeleteItemBlock = async () => {
  if (!itemBlockToDelete.value) return;
  
  try {
    isDeleting.value = true;
    await itemBlocksStore.delete(itemBlockToDelete.value.id);
    
    // Show success message
    toast.success(i18n.t('page.itemBlock.deletedSuccessfully', { 
      itemBlockCode: itemBlockToDelete.value.code 
    }), { autoClose: 3000 });
    
    hideDeleteModal();
    
  } catch (error) {
    console.error('Delete failed:', error);
    toast.error(i18n.t('error.apiError', { error: (error as Error).message }), { 
      autoClose: 10000 
    });
  } finally {
    isDeleting.value = false;
  }
};

onMounted(async () => {
  // await itemBlocksStore.fetchAll();
  if (selectedSiteId.value === 0) {
    toast.error('No site selected', { autoClose: 10000 });
  }
});
</script>
